import type { Map as MapLibreMap } from 'maplibre-gl'

import type { Layer } from './layer'
import { getLayerDef } from './layers/registry'
import { firstSymbolLayerId } from './layers/slots'
import { nearestIndex } from './layers/timeSource'

/**
 * Owns the overlay stack: which layers are on, in what order, at what opacity,
 * and which instant of the shared time axis is showing. Plain TypeScript, no
 * Svelte imports — animation timers do not belong in the reactivity graph.
 *
 * `layers.svelte.ts` mirrors this for the UI; `MapView.svelte` is the bridge.
 */

export interface ControllerState {
  /** The union of every active layer's frames, oldest first. */
  times: Date[]
  index: number
  /**
   * The last observed frame on the axis, when a layer's frames run on past it
   * into a forecast (the radar map's next hour); null when nothing on the
   * axis is a forecast. The slider marks it, and lands there.
   */
  nowIndex: number | null
  playing: boolean
  /** True while a newly activated layer is still fetching its frame list. */
  loading: boolean
}

/** 500 ms a frame reads as weather moving; faster is a flicker. */
const FRAME_MS = 500
/** Without a hold the loop has no visible beginning and is hard to read. */
const HOLD_MS = 1200

export class LayerController {
  private map: MapLibreMap
  private onChange: (s: ControllerState) => void
  private layers = new Map<string, Layer>()
  /** Bottom-first stack order; the last id draws on top. */
  private order: string[] = []
  private times: Date[] = []
  /** -1 until the axis exists or the user has scrubbed; see recomputeTimes(). */
  private index = -1
  /** Where observation ends on the axis; see `ControllerState.nowIndex`. */
  private nowIndex: number | null = null
  private observedUntil: Date | null = null
  /** Layers dragged to opacity 0, which stay off whatever the slider says. */
  private dimmed = new Set<string>()
  /** Observation-only layers switched off because the slider is past now. */
  private pastNow = new Set<string>()
  private playing = false
  private loading = false
  private raf: number | null = null
  private elapsed = 0
  /** -1, not 0: a rAF timestamp of 0 is legitimate on the first frame after load. */
  private lastTick = -1
  private generation = 0
  private styleReady = false
  private refreshing = false
  private destroyed = false
  private offStyleLoad: () => void

  constructor(map: MapLibreMap, onChange: (s: ControllerState) => void) {
    this.map = map
    this.onChange = onChange
    // A basemap switch destroys every style-owned source and layer. Re-add
    // them all, in order, before the next frame.
    const handler = () => {
      this.styleReady = true
      this.addAll()
    }
    map.on('style.load', handler)
    this.offStyleLoad = () => map.off('style.load', handler)
    // `style.load` always fires for the map's first style, so the usual path
    // in is that handler. This covers a style that somehow finished first.
    if (map.isStyleLoaded()) this.styleReady = true
  }

  isActive(id: string): boolean {
    return this.order.includes(id)
  }

  /**
   * The instant each active layer is drawing. Layers snap the shared axis
   * position to their own nearest frame, and those can be an hour apart when a
   * four-minute radar loop and an hourly forecast share a slider.
   */
  currentTimes(): Map<string, Date | null> {
    return new Map([...this.layers].map(([id, layer]) => [id, layer.currentTime()]))
  }

  /**
   * The live `Layer` objects, for a readout that needs more than a number out
   * of the network — a model layer knows which forecast hour it is drawing,
   * and nothing else does.
   */
  activeLayers(): ReadonlyMap<string, Layer> {
    return this.layers
  }

  /**
   * Tell the controller a basemap switch is starting.
   *
   * `setStyle` leaves the map without a style for a beat, and every MapLibre
   * mutation in that window throws "Style is not done loading" — which arrives
   * as a map *error* rather than a rejected promise. Nothing is lost by
   * skipping them: `style.load` re-adds every layer and re-applies time and
   * opacity.
   *
   * `map.isStyleLoaded()` is not the test to use here. It also requires every
   * source cache to have finished fetching, so it is false whenever tiles are
   * in flight — which, with twelve radar frames mounted, is most of the time.
   */
  suspend(): void {
    this.styleReady = false
  }

  /** `ids` is stack order, bottom first. */
  setActive(ids: string[]): void {
    const wanted = ids.filter((id) => getLayerDef(id))
    if (wanted.length === this.order.length && wanted.every((id, i) => this.order[i] === id)) {
      return
    }

    for (const [id, layer] of this.layers) {
      if (wanted.includes(id)) continue
      layer.remove(this.map)
      this.layers.delete(id)
      this.pastNow.delete(id)
    }

    this.order = wanted
    this.ensure()
  }

  /**
   * Drop and re-create the active layers matching `match`, keeping the stack.
   *
   * For a setting a layer reads when it is *constructed* rather than one it can
   * be told — the model run and the lead window, both of which decide which
   * frames are fetched at `load()`. `setActive` cannot do this: its first act
   * is to return early when the id list has not changed, which is exactly the
   * case here.
   */
  reload(match: (id: string) => boolean): void {
    const doomed = this.order.filter(match)
    if (doomed.length === 0) return
    for (const id of doomed) {
      this.layers.get(id)?.remove(this.map)
      this.layers.delete(id)
      this.pastNow.delete(id)
    }
    this.ensure()
  }

  /**
   * Ask every active layer whether upstream has moved on, and follow it.
   *
   * `load()` runs once per layer, so before this a radar loop was frozen at
   * the moment it was turned on: a phone reopened after an hour showed the
   * hour-old loop as current until somebody toggled the layer. `MapView` calls
   * this every couple of minutes while the page is visible and at once when it
   * comes back. Each layer gates on its own data's age, so a call that finds
   * nothing stale costs at most one capabilities read, and that one is cached.
   *
   * The slider stays on what it was showing: at the live end if it was at the
   * end — so a new radar frame arrives *as* the current frame — and otherwise
   * on the nearest instant to the one the user scrubbed to, not on the same
   * index, which after the loop slides is a different time.
   */
  async refresh(): Promise<void> {
    if (this.refreshing || this.destroyed) return
    this.refreshing = true
    const shown = this.times[this.index]
    const atEnd = this.index === this.liveIndex()
    try {
      const outcomes = await Promise.all(
        [...this.layers.values()].map((layer) => {
          // Still the live one, and a style to write into: asked at the
          // moment of writing, because a fetch can outlive both.
          const canPatch = () => this.styleReady && this.layers.get(layer.id) === layer
          return layer.refresh?.(canPatch) ?? Promise.resolve('unchanged' as const)
        }),
      )
      if (this.destroyed) return
      if (outcomes.includes('remount')) this.addAll()
      else if (outcomes.includes('patched')) this.recomputeTimes()
      else return
      if (this.times.length === 0) return
      this.index = atEnd || !shown ? this.liveIndex() : nearestIndex(this.times, shown)
      this.applyTime()
      this.emit()
    } finally {
      this.refreshing = false
    }
  }

  /**
   * Create every layer in `this.order` that does not exist yet, then mount the
   * whole stack once their frames have landed.
   */
  private ensure(): void {
    const wanted = this.order
    const created: Promise<void>[] = []
    for (const id of wanted) {
      if (this.layers.has(id)) continue
      const layer = getLayerDef(id)!.create()
      this.layers.set(id, layer)
      created.push(layer.load())
    }

    if (created.length === 0) {
      this.addAll()
      return
    }

    // A layer cannot be added before it knows its frames, so the whole stack
    // waits.
    const generation = ++this.generation
    this.loading = true
    this.emit()
    void Promise.all(created).then(() => {
      // `generation` decides who gets to clear `loading`, and nothing else.
      //
      // It used to gate the `addAll()` too, which was wrong: activating a
      // second layer while the first is still fetching makes the first batch
      // "stale", but the layer it was loading is still in the stack and still
      // needs mounting with the payload that has only now arrived. Whichever
      // batch resolves first mounted it empty, and nothing re-mounted it —
      // observed as an SPC outlook that drew nothing at all until its own
      // refresh timer fired five minutes later, which is indistinguishable
      // from a day with no outlook area. `addAll()` is idempotent and is what
      // every basemap switch already runs.
      if (generation === this.generation) this.loading = false
      this.addAll()
    })
  }

  /**
   * Who to credit for a layer that MapLibre's own `AttributionControl` cannot
   * see, or null when it can.
   *
   * The control reads `attribution` off **sources in use**. A custom WebGL
   * layer has no source, and a dummy one does not help because nothing marks
   * it `used` — so the data-texture layer states its credit itself and the
   * legend prints it. Null here means "MapLibre is already showing it", which
   * is what keeps the credit from appearing twice while both plate kinds are
   * in circulation.
   */
  attributionFor(id: string): string | null {
    return this.layers.get(id)?.ownAttribution?.() ?? null
  }

  setOpacity(id: string, opacity: number): void {
    const layer = this.layers.get(id)
    if (!layer || !this.styleReady) return
    layer.setOpacity(opacity)
    // A layer dragged to zero is off, not merely invisible: `visibility: none`
    // is what stops it fetching frames nobody can see.
    if (opacity > 0) this.dimmed.delete(id)
    else this.dimmed.add(id)
    layer.setVisible(opacity > 0 && !this.pastNow.has(id))
  }

  /**
   * True while a layer is switched off because the slider is past now. The
   * map menu leaves it out: a lightning reading from a frame the map is not
   * drawing describes nothing on screen.
   */
  isHiddenPastNow(id: string): boolean {
    return this.pastNow.has(id)
  }

  setTimeIndex(i: number): void {
    if (this.times.length === 0) return
    const next = Math.max(0, Math.min(this.times.length - 1, Math.round(i)))
    if (next === this.index) return
    this.index = next
    this.applyTime()
    this.emit()
  }

  play(): void {
    if (this.playing || this.times.length < 2) return
    this.playing = true
    this.elapsed = 0
    this.lastTick = -1
    // requestAnimationFrame, not setInterval: a background tab throttles rAF to
    // nothing and the loop simply pauses, where setInterval fires a burst of
    // catch-up frames on return and hammers the WMS.
    this.raf = requestAnimationFrame(this.tick)
    this.emit()
  }

  pause(): void {
    if (!this.playing) return
    this.playing = false
    if (this.raf !== null) cancelAnimationFrame(this.raf)
    this.raf = null
    this.emit()
  }

  destroy(): void {
    // Timer first. The other order leaves a tick writing to a removed source,
    // which MapLibre reports as a style error rather than a throw.
    this.pause()
    this.destroyed = true
    this.generation++
    this.offStyleLoad()
    for (const layer of this.layers.values()) layer.remove(this.map)
    this.layers.clear()
    this.order = []
  }

  private tick = (now: number): void => {
    if (!this.playing) return
    if (this.lastTick < 0) this.lastTick = now
    this.elapsed += now - this.lastTick
    this.lastTick = now

    const last = this.index === this.times.length - 1
    if (this.elapsed >= (last ? HOLD_MS : FRAME_MS)) {
      this.elapsed = 0
      this.index = last ? 0 : this.index + 1
      this.applyTime()
      this.emit()
    }
    this.raf = requestAnimationFrame(this.tick)
  }

  /** Re-add every active layer, bottom first, under the basemap's labels. */
  private addAll(): void {
    if (!this.styleReady) return
    const beforeId = firstSymbolLayerId(this.map)
    // Same `beforeId` for every layer, added bottom-first: each insert lands
    // after the previous one and still above the basemap, which is exactly the
    // stack order asked for.
    for (const id of this.order) {
      const layer = this.layers.get(id)
      if (!layer) continue
      layer.remove(this.map)
      layer.add(this.map, beforeId)
    }
    this.recomputeTimes()
    this.applyTime()
    this.emit()
  }

  /**
   * One shared axis: the union of the active layers' frames. A radar loop and
   * an NDFD forecast have wildly different cadences, and a layer whose own
   * frames do not cover an instant snaps to its nearest — which is what
   * nowCOAST's `nearestValue="1"` does server-side anyway.
   */
  private recomputeTimes(): void {
    const seen = new Map<number, Date>()
    for (const id of this.order) {
      for (const t of this.layers.get(id)?.times ?? []) {
        // new Date(0) is the wmsTimeLayer sentinel for "no dimension"; it is
        // not a real instant and must not appear on the slider.
        if (t.getTime() !== 0) seen.set(t.getTime(), t)
      }
    }
    this.times = [...seen.values()].sort((a, b) => a.getTime() - b.getTime())
    // Where observation ends: the earliest any layer says, so that no
    // observed frame on the axis is ever called a forecast.
    const ends = this.order
      .map((id) => this.layers.get(id)?.observedUntil?.() ?? null)
      .filter((t): t is Date => t !== null)
    this.observedUntil = ends.length ? new Date(Math.min(...ends.map((t) => t.getTime()))) : null
    const until = this.observedUntil?.getTime()
    let last = -1
    if (until !== undefined) {
      this.times.forEach((t, i) => {
        if (t.getTime() <= until) last = i
      })
    }
    this.nowIndex = last >= 0 && last < this.times.length - 1 ? last : null
    if (this.times.length === 0) {
      this.index = -1
      this.pause()
      return
    }
    // Land on now — the newest frame, or the newest observed one when a
    // forecast follows it — until the user has scrubbed somewhere themselves.
    // A forecast is something to scrub into, not the first thing shown.
    this.index = this.index < 0 ? this.liveIndex() : Math.min(this.index, this.times.length - 1)
  }

  /** The frame that is "now" on this axis: see `recomputeTimes`. */
  private liveIndex(): number {
    return this.nowIndex ?? this.times.length - 1
  }

  /**
   * Show the instant, and past now switch off every layer that only
   * observes: a lightning or satellite frame held under the radar's next hour
   * reads as a forecast of lightning, and there is none. Layers on no clock
   * (alerts, the SPC products) are current products and stay.
   */
  private applyTime(): void {
    const t = this.times[this.index] ?? undefined
    if (!t || !this.styleReady) return
    const past = this.observedUntil !== null && t.getTime() > this.observedUntil.getTime()
    for (const [id, layer] of this.layers) {
      const observes =
        getLayerDef(id)?.timeline === 'live' && (layer.observedUntil?.() ?? null) === null
      const hide = past && observes
      if (hide !== this.pastNow.has(id)) {
        if (hide) this.pastNow.add(id)
        else this.pastNow.delete(id)
        layer.setVisible(!hide && !this.dimmed.has(id))
      }
      layer.setTime(t)
    }
  }

  private emit(): void {
    this.onChange({
      times: this.times,
      index: Math.max(0, this.index),
      nowIndex: this.nowIndex,
      playing: this.playing,
      loading: this.loading,
    })
  }
}
